import React, { useState, useEffect } from "react";
import { Form, Input, Button, Empty, Modal, message } from "antd";
import Swal from "sweetalert2";
import { ToastContainer } from "react-toastify";
import { config } from "../../config/config";
import CryptoJS from "crypto-js";
import axios from "axios";
import { format } from "date-fns";
import { EyeOutlined } from "@ant-design/icons";

const SECRET_KEY = import.meta.env.VITE_APP_SECRET_KEY;

function decryptData(ciphertext) {
  if (!ciphertext) return null;
  try {
    const bytes = CryptoJS.AES.decrypt(ciphertext, SECRET_KEY);
    const decrypted = bytes.toString(CryptoJS.enc.Utf8);
    return JSON.parse(decrypted);
  } catch {
    return null;
  }
}

function getFirstAndLastName(item) {
  if (!item) return "pending update";
  const firstName = item.firstName || item.firstname || item.first_name || "";
  const lastName = item.lastName || item.lastname || item.last_name || "";
  if (firstName || lastName) {
    return `${firstName} ${lastName}`.trim();
  }
  return item.fullName || item.fullname || item.full_name || item.name || "pending update";
}

function BvnCrm() {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [selectedTicket, setSelectedTicket] = useState(null);
  const [isTicketModalVisible, setIsTicketModalVisible] = useState(false);
  const [fetchingTicket, setFetchingTicket] = useState(false);
  const [amount, setAmount] = useState(1500); // Default amount
  const [priceLoading, setPriceLoading] = useState(true);

  const showTicketDetails = async (ticketId) => {
    if (!ticketId) return;
    setFetchingTicket(true);
    try {
      const endpoint = config.endpoints.bvnCrmTicket || "/bvncrm/ticket/";
      const response = await axios.get(
        `${config.apiBaseUrl}${endpoint}${ticketId}`,
        {
          withCredentials: true,
        }
      );
      const data = response.data?.data || response.data?.ticket || response.data;
      if (data) {
        setSelectedTicket(data);
        setIsTicketModalVisible(true);
      } else {
        throw new Error("No details found for this ticket ID.");
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Query Failed",
        text: error.response?.data?.message || error.message || "Failed to fetch ticket details.",
        confirmButtonColor: "#f59e0b",
      });
    } finally {
      setFetchingTicket(false);
    }
  };

  // Get userId and userPhone from encrypted localStorage
  let userId = null;
  let userPhone = "";
  try {
    const userStr = localStorage.getItem("user");
    if (userStr) {
      const userObj = decryptData(userStr);
      userId = userObj?._id || userObj?.id;
      userPhone = userObj?.phone || userObj?.phoneNumber || "";
    }
  } catch (error) {}

  useEffect(() => {
    if (userPhone) {
      form.setFieldsValue({ phone: userPhone });
    }
  }, [userPhone, form]);

  // Fetch BVN CRM pricing
  useEffect(() => {
    const fetchPrices = async () => {
      setPriceLoading(true);
      try {
        const response = await axios.get(
          `${config.apiBaseUrl}${config.endpoints.currentapipricing}`,
          { withCredentials: true }
        );
        // Find BVN CRM pricing
        const bvnCrmPricingData = Array.isArray(response.data)
          ? response.data.find((item) => item.key === "bvn_crm")
          : response.data;

        const bvnCrmPricing =
          bvnCrmPricingData?.key === "bvn_crm" ? bvnCrmPricingData : null;

        if (bvnCrmPricing && bvnCrmPricing.prices) {
          setAmount(bvnCrmPricing.prices.agent);
        }
      } catch (error) {
        message.error("Failed to fetch current price");
      } finally {
        setPriceLoading(false);
      }
    };

    fetchPrices();
  }, []);

  const getStatusLabel = (status) => {
    const s = String(status || "").toLowerCase();
    if (s === "successfully" || s === "success" || s === "completed") return "Successful";
    if (s === "pending") return "Pending";
    return "Rejected";
  };

  const getStatusClass = (status) => {
    const s = getStatusLabel(status);
    if (s === "Successful") return "bg-green-100 text-green-800";
    if (s === "Pending") return "bg-blue-100 text-blue-800";
    return "bg-red-100 text-red-800";
  };

  const fetchHistory = async () => {
    if (!userId) return;
    setLoadingHistory(true);
    try {
      const response = await axios.get(
        `${config.apiBaseUrl}${config.endpoints.bvnCrmHistory || "/bvncrm/user/"}${userId}`,
        {
          withCredentials: true,
          headers: { "Content-Type": "application/json" },
        }
      );
      
      let rawData = response.data;
      let crmData = null;

      if (Array.isArray(rawData)) {
        crmData = rawData;
      } else if (rawData && typeof rawData === "object") {
        const keysToTry = ["tickets", "submissions", "bvnCrm", "bvncrm", "history", "data", "requests", "crm", "license", "modify"];
        for (const key of keysToTry) {
          if (Array.isArray(rawData[key])) {
            crmData = rawData[key];
            break;
          }
        }
        if (!crmData && rawData.data && typeof rawData.data === "object") {
          if (Array.isArray(rawData.data)) {
            crmData = rawData.data;
          } else {
            for (const key of keysToTry) {
              if (Array.isArray(rawData.data[key])) {
                crmData = rawData.data[key];
                break;
              }
            }
          }
        }
      }

      const list = Array.isArray(crmData) ? crmData : [];
      list.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      setHistory(list);
    } catch (error) {
      
    } finally {
      setLoadingHistory(false);
    }
  };

  useEffect(() => {
    fetchHistory();
  }, [userId]);

  const onFinish = async (values) => {
    // Show confirmation dialog first
    const result = await Swal.fire({
      title: "Confirm BVN CRM Request",
      html: `
        <p class="mb-4 text-gray-500">Are you sure you want to submit this BVN CRM request?</p>
        <div class="bg-slate-50 border border-slate-100 rounded-2xl p-4 space-y-2 text-left mb-4">
          <p class="text-sm text-gray-600"><strong>Batch ID:</strong> ${values.batchId}</p>
          <p class="text-sm text-gray-600"><strong>Ticket ID:</strong> ${values.ticketId}</p>
        </div>
        <div class="bg-sky-50 border-2 border-sky-200 rounded-2xl py-3 px-6 inline-block shadow-sm">
          <span class="text-xs font-bold text-sky-500 uppercase tracking-wider block mb-0.5">Verification Fee</span>
          <span class="text-3xl font-black text-sky-600">₦${amount.toLocaleString()}</span>
        </div>
      `,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#f59e0b",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, submit",
      cancelButtonText: "Cancel",
    });

    if (!result.isConfirmed) {
      return;
    }

    setLoading(true);
    try {
      if (!userId) {
        throw new Error("User ID not found");
      }

      const payload = {
        userId,
        phone: userPhone,
        batchid: values.batchId,
        ticketid: values.ticketId,
        amount: amount,
      };

      const endpoint = config.endpoints.bvnCrm || "/bvncrm/create";
      
      const response = await axios.post(
        `${config.apiBaseUrl}${endpoint}/${userId}`,
        payload,
        {
          withCredentials: true,
        }
      );

      await Swal.fire({
        icon: "success",
        title: "Submission Successful!",
        text: "Your BVN CRM request has been submitted successfully.",
        confirmButtonColor: "#f59e0b",
      });
      form.resetFields();
      fetchHistory();
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Submission Failed",
        text:
          error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Failed to submit request. Please try again.",
        confirmButtonColor: "#f59e0b",
      });
    } finally {
      setLoading(false);
    }
  };

  const totalPages = Math.ceil(history.length / pageSize);
  const paginatedData = history.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  return (
    <div className="w-full">
      <div className="w-full rounded-2xl mb-5 bg-white p-5 md:p-10 shadow-lg max-w-3xl mx-auto">
        <ToastContainer />
        <div className="flex flex-col items-center justify-center gap-3 mb-6">
          <h2 className="text-[clamp(1.5rem,3vw,2.2rem)] font-extrabold text-slate-800 tracking-tight text-center">
            Central Risk Management of BVN
          </h2>
          <div className="bg-sky-50 px-4 py-2 rounded-2xl border-2 border-sky-200 text-sky-600 font-black text-lg shadow-sm whitespace-nowrap">
            {priceLoading ? "Loading price..." : `₦${amount.toLocaleString()}`}
          </div>
        </div>
        <p className="text-center text-gray-500 mb-8">
          Submit your Batch ID and Ticket ID. Note: A fee of <span className="font-bold text-green-600">{priceLoading ? "..." : `₦${amount.toLocaleString()}`}</span> will be charged per request.
        </p>
        
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          requiredMark={true}
          size="large"
        >
          <Form.Item
            name="batchId"
            label={<span className="font-medium text-gray-700">Batch ID</span>}
            rules={[{ required: true, message: "Please enter your Batch ID" }]}
          >
            <Input placeholder="Enter Batch ID" className="h-12" />
          </Form.Item>

          <Form.Item
            name="ticketId"
            label={<span className="font-medium text-gray-700">Ticket ID</span>}
            rules={[{ required: true, message: "Please enter your Ticket ID" }]}
          >
            <Input placeholder="Enter Ticket ID" className="h-12" />
          </Form.Item>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6 mt-4">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-yellow-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-yellow-800">
                  Payment Confirmation
                </h3>
                <div className="mt-2 text-sm text-yellow-700">
                  <p>
                    By submitting this form, you agree to a deduction of <strong>{priceLoading ? "..." : `₦${amount.toLocaleString()}`}</strong> from your wallet balance.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <Form.Item className="mb-0">
            <Button
              type="primary"
              size="large"
              htmlType="submit"
              className="w-full flex items-center justify-center bg-sky-500 hover:bg-sky-600 h-12 text-lg font-medium"
              loading={loading}
            >
              Submit Request {priceLoading ? "" : `(₦${amount.toLocaleString()})`}
            </Button>
          </Form.Item>
        </Form>
      </div>

      {/* History Table */}
      <div className="w-full mt-10">
        <h3 className="text-[18px] text-gray-500 mb-4 px-2">Central Risk Management of BVN Request History</h3>
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100">
          <div className="overflow-x-auto">
            <table className="w-full whitespace-nowrap">
              <thead className="bg-gray-50/50 border-b border-gray-100">
                <tr>
                  <th className="px-6 py-5 text-left text-[15px] font-semibold text-gray-700">S/N</th>
                  <th className="px-6 py-5 text-left text-[15px] font-semibold text-gray-700">Ticket ID</th>
                  <th className="px-6 py-5 text-left text-[15px] font-semibold text-gray-700">Status</th>
                  <th className="px-6 py-5 text-left text-[15px] font-semibold text-gray-700">First & Last Name</th>
                  <th className="px-6 py-5 text-left text-[15px] font-semibold text-gray-700">Bvn</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {loadingHistory ? (
                  <tr>
                    <td colSpan="5" className="px-6 py-8 text-center text-gray-500">
                      Loading history...
                    </td>
                  </tr>
                ) : history.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="px-6 py-8 text-center">
                      <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No history found" />
                    </td>
                  </tr>
                ) : (
                  paginatedData.map((item, idx) => {
                    const statusText = getStatusLabel(item.status);
                    const statusClass = getStatusClass(item.status);

                    const itemTicketId = item.ticketid || item.ticketId;

                    return (
                      <tr key={idx} className="hover:bg-gray-50/50 transition-colors">
                        <td className="px-6 py-5 text-[15px] text-gray-800">
                          {(currentPage - 1) * pageSize + idx + 1}
                        </td>
                        <td className="px-6 py-5 text-[15px] text-gray-800 font-mono">
                          {itemTicketId || "pending update"}
                        </td>
                        <td className="px-6 py-5">
                          <span className={`px-2 py-0.5 inline-flex text-xs leading-5 rounded-full capitalize font-medium ${statusClass}`}>
                            {statusText}
                          </span>
                        </td>
                        <td className="px-6 py-5 text-[15px] text-gray-800">
                          {getFirstAndLastName(item)}
                        </td>
                        <td className="px-6 py-5 text-[15px] text-gray-800 font-mono">
                          {item.bvn || "pending update"}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex flex-col sm:flex-row items-center justify-between p-6 gap-4 border-t border-gray-100">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <span>Show:</span>
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="border rounded px-2 py-1 text-sm bg-white"
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </select>
                <span>entries</span>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 border rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 bg-white"
                >
                  Previous
                </button>

                <span className="text-sm px-3 text-gray-600">
                  Page {currentPage} of {totalPages}
                </span>

                <button
                  onClick={() =>
                    setCurrentPage(Math.min(totalPages, currentPage + 1))
                  }
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 border rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 bg-white"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      {/* Ticket Details Modal */}
      <Modal
        title="BVN CRM Ticket Status Details"
        open={isTicketModalVisible}
        onCancel={() => setIsTicketModalVisible(false)}
        footer={[
          <Button
            key="close"
            type="primary"
            onClick={() => setIsTicketModalVisible(false)}
            className="bg-sky-500 hover:bg-sky-600 font-semibold px-6 rounded-lg"
          >
            Close
          </Button>
        ]}
        width={550}
      >
        {selectedTicket && (
          <div className="p-4 space-y-4">
            <div className="flex justify-between items-center border-b pb-3 mb-3">
              <div>
                <p className="text-xs text-gray-400 uppercase font-black tracking-wider">Ticket ID</p>
                <p className="text-lg font-bold font-mono text-gray-800">{selectedTicket.ticketid || selectedTicket.ticketId || "N/A"}</p>
              </div>
              <span className={`px-3 py-1 text-xs leading-5 font-semibold rounded-full capitalize ${getStatusClass(selectedTicket.status)}`}>
                {getStatusLabel(selectedTicket.status)}
              </span>
            </div>

            <div className="grid grid-cols-2 gap-y-4 gap-x-2">
              <div>
                <p className="text-xs text-gray-400 uppercase font-black tracking-wider">First & Last Name</p>
                <p className="text-sm font-semibold text-gray-800">{getFirstAndLastName(selectedTicket)}</p>
              </div>
              <div>
                <p className="text-xs text-gray-400 uppercase font-black tracking-wider">BVN Number</p>
                <p className="text-sm font-semibold font-mono text-gray-800">
                  {selectedTicket.bvn || "N/A"}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-400 uppercase font-black tracking-wider">Batch ID</p>
                <p className="text-sm font-semibold font-mono text-gray-800">{selectedTicket.batchid || selectedTicket.batchId || "N/A"}</p>
              </div>
              <div>
                <p className="text-xs text-gray-400 uppercase font-black tracking-wider">Phone Number</p>
                <p className="text-sm font-semibold text-gray-800">{selectedTicket.phone || "N/A"}</p>
              </div>
              <div>
                <p className="text-xs text-gray-400 uppercase font-black tracking-wider">Submission Date</p>
                <p className="text-sm font-semibold text-gray-800">
                  {selectedTicket.createdAt ? format(new Date(selectedTicket.createdAt), "dd/MM/yyyy HH:mm") : "-"}
                </p>
              </div>
            </div>

            {selectedTicket.reply && (
              <div className="bg-gray-50 border border-gray-100 rounded-xl p-4 mt-3">
                <p className="text-xs text-gray-400 uppercase font-black tracking-wider mb-1">Reply / Comments</p>
                <p className="text-sm text-gray-700 font-medium break-words leading-relaxed">
                  {selectedTicket.reply}
                </p>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}

export default BvnCrm;
